import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { INITIAL_PAGE, PAGINATION_LIMITS } from '@repo/toolkit/constants/pagination'
import { useState } from 'react'
import ReactPaginate, { type ReactPaginateProps } from 'react-paginate'

import useTranslate from '../../../../../apps/backoffice/src/lib/hooks/use-translate'
import { CheckMarkIcon, ChevronDownIcon } from '../../../icons'
import ArrowLeftOneIcon from '../../../icons/arrow-left-01'
import ArrowRightOneIcon from '../../../icons/arrow-right-01'
import Button from '../../atoms/button'
import Popover from '../../atoms/popover'
import Typography from '../../atoms/typography'

interface PaginationProps extends ReactPaginateProps {
  page: number
  limit: number
  total: number
  limitControl?: boolean
  onLimitChange: (limit: number) => void
}

const Pagination = ({
  page,
  limit,
  total,
  limitControl = true,
  onLimitChange,
  ...props
}: PaginationProps) => {
  const pageCount = props.pageCount ?? 0
  const safeForcePage = pageCount > 0 ? Math.min(page, pageCount - 1) : undefined
  const isFirstPage: boolean = page === INITIAL_PAGE
  const isLastPage: boolean = page === Math.ceil(total / limit) - 1

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const { t } = useTranslate()

  return (
    <div className={'mt-4 flex items-center justify-between flex-wrap gap-3'}>
      {limitControl && (
        <div className="border border-grey-light rounded p-2 flex items-center gap-2 ">
          <Typography variant="label-medium">{t('common.pagination.show')}</Typography>
          <Popover
            isOpen={isPopoverOpen}
            triggerComponent={(open) => (
              <div
                className={cn(
                  'flex items-center gap-1 bg-primary-light px-2 rounded-sm cursor-pointer',
                  open && 'text-primary'
                )}
              >
                <Typography variant="label-medium">{limit}</Typography>
                <ChevronDownIcon
                  color={tailwindColors.grey.DEFAULT}
                  className={cn('transition-transform duration-300', {
                    'transform rotate-180': open
                  })}
                />
              </div>
            )}
            onOpenChange={setIsPopoverOpen}
            className="min-w-10  px-2 drop-shadow-2xl group"
          >
            <div className="flex flex-col gap-3 w-full py-2">
              {PAGINATION_LIMITS.map((page) => (
                <button
                  key={page}
                  onClick={() => {
                    onLimitChange(page)
                    setIsPopoverOpen(false)
                  }}
                >
                  <div className="flex items-center gap-3 hover:bg-primary-light p-2 rounded ">
                    <Typography
                      variant="label-medium"
                      color={tailwindColors.grey.DEFAULT}
                      className=" rounded-sm "
                    >
                      {page}
                    </Typography>
                    {page === limit && (
                      <CheckMarkIcon
                        color={tailwindColors.primary.DEFAULT}
                        height="15"
                        width="15"
                      />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </Popover>
          <Typography variant="label-medium" className="whitespace-nowrap">
            {t('common.pagination.per.page')}
          </Typography>
        </div>
      )}

      {(!isFirstPage || !isLastPage) && (
        <ReactPaginate
          breakLabel={
            <div className="w-[35px] h-[35px] flex items-center justify-center rounded text-xs">
              {'···'}
            </div>
          }
          previousLabel={
            <div className="flex items-center mr-2">
              <Button
                variant="secondary"
                icon={ArrowLeftOneIcon}
                disabled={isFirstPage}
                label={t('common.pagination.previous')}
                className={cn('text-black p-2')}
                iconColor={tailwindColors.black.DEFAULT}
              />
            </div>
          }
          nextLabel={
            <div className="flex items-center ml-2">
              <Button
                variant="secondary"
                icon={ArrowRightOneIcon}
                disabled={isLastPage}
                label={t('common.pagination.next')}
                className={cn('text-black flex-row-reverse p-2')}
                iconColor={tailwindColors.black.DEFAULT}
              />
            </div>
          }
          pageRangeDisplayed={1}
          marginPagesDisplayed={2}
          renderOnZeroPageCount={null}
          pageClassName={'w-[35px] h-[35px] flex items-center justify-center rounded text-xs'}
          containerClassName={'flex text-grey items-center'}
          activeClassName={'bg-primary-light text-primary'}
          forcePage={safeForcePage}
          {...props}
        />
      )}
    </div>
  )
}

export default Pagination
